/**
 * Composant "Leaderboard Cooper du mois" (V33 — gamification).
 *
 * Affiche le top 10 des Coopers par nombre de parrainages signés sur le mois
 * en cours, avec leur bonus reçu et le rang du Cooper connecté.
 *
 * Intégré dans CooperDashboard (onglet KPI). Toggle Mois courant / 30 jours.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Trophy, Crown, Medal, Award, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const rankIcon = (rank) => {
  if (rank === 1) return <Crown className="h-5 w-5 text-amber-400" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-slate-400" />;
  if (rank === 3) return <Award className="h-4 w-4 text-orange-400" />;
  return <span className="text-xs text-slate-500 font-mono w-5 text-center">#{rank}</span>;
};

const CooperLeaderboard = ({ getAuthHeader, currentUserId }) => {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/cooper/leaderboard`, {
        params: { period, limit: 10 },
        headers: getAuthHeader(),
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const myRank = data?.my_rank;

  return (
    <Card className="border-l-4 border-l-amber-400" data-testid="cooper-leaderboard">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Cooper du mois
            </CardTitle>
            <CardDescription className="text-xs">
              Classement des Coopers par parrainages signés
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant={period === 'month' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setPeriod('month')}
              data-testid="cooper-lb-tab-month"
            >
              Mois
            </Button>
            <Button
              size="sm"
              variant={period === '30d' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setPeriod('30d')}
              data-testid="cooper-lb-tab-30d"
            >
              30 jours
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-400 text-center py-4">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-6 text-slate-400" data-testid="cooper-lb-empty">
            <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="text-xs">Pas encore de parrainages ce mois-ci.</p>
            <p className="text-[11px] mt-1">Soyez le premier à entrer dans le classement.</p>
          </div>
        ) : (
          <>
            <ul className="space-y-1" data-testid="cooper-lb-list">
              {items.map((entry) => {
                const isMe = entry.cooper_id === currentUserId;
                return (
                  <li
                    key={entry.cooper_id}
                    className={`flex items-center justify-between gap-2 px-2 py-2 rounded text-sm ${
                      isMe
                        ? 'bg-indigo-50 ring-1 ring-indigo-200'
                        : entry.rank <= 3
                        ? 'bg-amber-50/60'
                        : 'bg-slate-50/60'
                    }`}
                    data-testid={`cooper-lb-row-${entry.rank}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="shrink-0">{rankIcon(entry.rank)}</span>
                      <span className="truncate">
                        <span className="font-medium text-slate-700">
                          {entry.prenom || ''} {entry.nom || ''}
                        </span>
                        {isMe && (
                          <Badge variant="outline" className="ml-2 text-[10px] border-indigo-300 text-indigo-700">
                            Vous
                          </Badge>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 text-xs">
                      <Badge variant="secondary">
                        {entry.parrainages} parrainage{entry.parrainages > 1 ? 's' : ''}
                      </Badge>
                      {entry.bonus_credits > 0 && (
                        <Badge className="bg-emerald-600 text-white font-semibold">
                          +{entry.bonus_credits} cr.
                        </Badge>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {myRank && myRank > items.length && (
              <p className="text-[11px] text-slate-400 mt-2 text-center" data-testid="cooper-lb-myrank">
                Votre rang actuel : #{myRank}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CooperLeaderboard;
